// Identity importers for the scenario-builder template family.
//
// Unlike the legacy Lesson Designer (whose JSON has a flat `{n, color, label,
// text, options}` shape that needs hydrating), the Scenario Builder agent
// pipeline emits data already shaped to the template's `SlideData` contract.
// So each importer here is a thin validator + id generator — preserving the
// agent's output verbatim, only filling in missing ids and clamping array
// counts.

import type {
  RevealPredictSlideData,
  ConfidenceMeterSlideData,
  ReflectActSlideData,
  BeforeAfterBridgeSlideData,
  SpotTheErrorSlideData,
  ClaimEvidenceSlideData,
  GalleryWalkSlideData,
  PerspectiveLensesSlideData,
  ScaffoldedInvestigationSlideData,
  LayeredRevealSlideData,
  TimelineNavigatorSlideData,
  ProgressiveBuilderSlideData,
  GuidedSandboxSlideData,
  DragSortSlideData,
  TensionMapperSlideData,
  ScenarioSimulationSlideData,
  TeachBackSlideData,
  TeachBackCoachKind,
  ScenarioExhibit,
  HotspotExplorerSlideData,
  SourceExaminationSlideData,
  SourceExaminationQuestion,
  MarkThisAnswerSlideData,
  MarkThisAnswerSampleAnswer,
  RevisionExamSlideData,
  RevisionExamSource,
  RevisionExamQuestion,
  BridgeSlideData,
  ComposedExperienceSlideData,
  SlideData,
} from '../../components/course-builder/slides/slideTypes';
import type { CompositionBlock } from './composition/types';
import type { DesignerSlide } from '../lessonImport';

/**
 * Registry of scenario-builder importers by template id. Extended as each
 * Phase 4 template lands. The AI panel reads from this to apply the
 * Builder's output to the slide regardless of which template was generated.
 */
export type ScenarioImporter = (ds: DesignerSlide, warnings: string[]) => SlideData;

/**
 * Importer for reveal-predict. Accepts a Designer-style slide whose `data`
 * field already carries the RevealPredictSlideData shape (the Scenario Builder
 * pipeline emits that directly). Generates UUIDs for any missing prediction
 * ids and enforces 2-4 options per prediction with stable letter values.
 */
export function buildRevealPredict(
  ds: DesignerSlide,
  warnings: string[],
): RevealPredictSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<RevealPredictSlideData> | undefined;
  if (!raw || !Array.isArray(raw.predictions)) {
    warnings.push('reveal-predict: missing or malformed `predictions` array');
    return {
      template: 'reveal-predict',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      predictions: [],
      summary: raw?.summary ?? { prompt: '' },
    };
  }

  return {
    template: 'reveal-predict',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    predictions: raw.predictions.map((p, idx) => {
      const id = p.id || crypto.randomUUID();
      const opts = Array.isArray(p.options) ? p.options.slice(0, 4) : [];
      if (opts.length < 2) {
        warnings.push(`reveal-predict: prediction ${idx + 1} has fewer than 2 options`);
      }
      // Normalise option values to a–d so the Builder doesn't have to.
      const options = opts.map((opt, optIdx) => ({
        value: opt.value || String.fromCharCode(97 + optIdx),
        text: opt.text ?? '',
      }));
      if (!options.some((o) => o.value === p.correctValue)) {
        warnings.push(
          `reveal-predict: prediction ${idx + 1} correctValue "${p.correctValue}" is not among the options`,
        );
      }
      return {
        id,
        question: p.question ?? '',
        context: p.context,
        options,
        correctValue: p.correctValue ?? options[0]?.value ?? 'a',
        revealAnswer: p.revealAnswer ?? '',
        matchFeedback: p.matchFeedback ?? '',
        missFeedback: p.missFeedback ?? '',
      };
    }),
    summary: raw.summary ?? { prompt: '' },
  };
}

/**
 * Importer for confidence-meter. Generates UUIDs for missing item ids,
 * enforces 3 scale labels (low/mid/high), clamps min/max to a sensible range,
 * and warns on missing content or out-of-range scale labels.
 */
export function buildConfidenceMeter(
  ds: DesignerSlide,
  warnings: string[],
): ConfidenceMeterSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ConfidenceMeterSlideData> | undefined;
  if (!raw || !Array.isArray(raw.items)) {
    warnings.push('confidence-meter: missing or malformed `items` array');
    return {
      template: 'confidence-meter',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      items: [],
      summary: raw?.summary ?? { prompt: '' },
    };
  }

  return {
    template: 'confidence-meter',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    items: raw.items.map((item, idx) => {
      const id = item.id || crypto.randomUUID();
      if (!item.content || !item.content.trim()) {
        warnings.push(`confidence-meter: item ${idx + 1} has no revealed content`);
      }
      // Clamp scale to [1, 7] and ensure 3 labels.
      const min = Math.min(Math.max(item.scale?.min ?? 1, 1), 4);
      const max = Math.min(Math.max(item.scale?.max ?? 5, min + 1), 7);
      const labels = Array.isArray(item.scale?.labels) ? item.scale.labels : [];
      const normalisedLabels: [string, string, string] = [
        labels[0] ?? 'Not at all',
        labels[1] ?? 'Somewhat',
        labels[2] ?? 'Very confident',
      ];
      if (labels.length !== 3) {
        warnings.push(`confidence-meter: item ${idx + 1} scale must have exactly 3 labels`);
      }
      return {
        id,
        statement: item.statement ?? '',
        content: item.content ?? '',
        scale: { min, max, labels: normalisedLabels },
      };
    }),
    summary: raw.summary ?? { prompt: '' },
  };
}

/**
 * Importer for reflect-act. Enforces the fixed 3-stage shape
 * (what → so-what → now-what), generates UUIDs for missing prompt ids,
 * and warns on missing labels or prompts.
 */
export function buildReflectAct(ds: DesignerSlide, warnings: string[]): ReflectActSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ReflectActSlideData> | undefined;
  if (!raw || !Array.isArray(raw.stages)) {
    warnings.push('reflect-act: missing or malformed `stages` array');
  }

  const STAGE_DEFAULTS = [
    { id: 'what' as const, label: 'What' },
    { id: 'so-what' as const, label: 'So what' },
    { id: 'now-what' as const, label: 'Now what' },
  ];

  type StageId = 'what' | 'so-what' | 'now-what';
  type Stage = ReflectActSlideData['stages'][number];

  const normaliseStage = (idx: number, source: Partial<Stage> | undefined, fallbackId: StageId, fallbackLabel: string): Stage => {
    const id = (source?.id as StageId) || fallbackId;
    if (id !== fallbackId) {
      warnings.push(`reflect-act: stage ${idx + 1} id should be "${fallbackId}", got "${id}"`);
    }
    if (!source?.label) {
      warnings.push(`reflect-act: stage ${idx + 1} is missing a label`);
    }
    const prompts = Array.isArray(source?.prompts) ? source!.prompts : [];
    if (prompts.length === 0) {
      warnings.push(`reflect-act: stage "${fallbackId}" has no prompts`);
    }
    return {
      id: fallbackId,
      label: source?.label ?? fallbackLabel,
      description: source?.description ?? '',
      prompts: prompts.map((p, pIdx) => ({
        id: p.id || crypto.randomUUID(),
        text: p.text ?? '',
        inputType: p.inputType === 'select' ? 'select' : 'text',
        options: p.inputType === 'select' ? (Array.isArray(p.options) ? p.options : []) : undefined,
        maxLength: p.maxLength,
        placeholder: p.placeholder,
        ...(p.text ? {} : (warnings.push(`reflect-act: stage "${fallbackId}" prompt ${pIdx + 1} has no text`), {})),
      })),
    } as Stage;
  };

  const stages = raw?.stages ?? [];
  return {
    template: 'reflect-act',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    stimulus: raw?.stimulus,
    stages: [
      normaliseStage(0, stages[0] as Partial<Stage>, STAGE_DEFAULTS[0].id, STAGE_DEFAULTS[0].label) as ReflectActSlideData['stages'][0],
      normaliseStage(1, stages[1] as Partial<Stage>, STAGE_DEFAULTS[1].id, STAGE_DEFAULTS[1].label) as ReflectActSlideData['stages'][1],
      normaliseStage(2, stages[2] as Partial<Stage>, STAGE_DEFAULTS[2].id, STAGE_DEFAULTS[2].label) as ReflectActSlideData['stages'][2],
    ],
    summary: raw?.summary ?? { showFullReflection: true, exportEnabled: false },
  };
}

/**
 * Importer for before-after-bridge. Generates UUIDs for missing prompt and
 * block ids, enforces that every before-phase prompt has a matching
 * after-phase entry (and vice versa) by id, normalises scale labels to a
 * pair, and warns on orphan prompts or missing instructions.
 */
export function buildBeforeAfterBridge(
  ds: DesignerSlide,
  warnings: string[],
): BeforeAfterBridgeSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<BeforeAfterBridgeSlideData> | undefined;
  if (!raw) {
    warnings.push('before-after-bridge: missing or malformed data');
    return emptyBeforeAfterBridge();
  }

  const beforePrompts = Array.isArray(raw.beforePhase?.prompts) ? raw.beforePhase!.prompts : [];
  const afterPrompts = Array.isArray(raw.afterPhase?.prompts) ? raw.afterPhase!.prompts : [];

  // Stamp UUIDs on any prompt missing one. We do this BEFORE the matching pass
  // so pairs that lost their id (rare LLM glitch) still pair by position.
  const beforeWithIds = beforePrompts.map((p, idx) => ({
    ...p,
    id: p.id || afterPrompts[idx]?.id || crypto.randomUUID(),
  }));
  const afterWithIds = afterPrompts.map((p, idx) => ({
    ...p,
    id: p.id || beforeWithIds[idx]?.id || crypto.randomUUID(),
  }));

  // Warn on orphans (after-prompt with no before counterpart, or vice versa).
  const beforeIds = new Set(beforeWithIds.map((p) => p.id));
  const afterIds = new Set(afterWithIds.map((p) => p.id));
  for (const id of beforeIds) {
    if (!afterIds.has(id)) {
      warnings.push(`before-after-bridge: before prompt "${id}" has no after counterpart`);
    }
  }
  for (const id of afterIds) {
    if (!beforeIds.has(id)) {
      warnings.push(`before-after-bridge: after prompt "${id}" has no before counterpart`);
    }
  }

  const blocks = Array.isArray(raw.contentPhase?.blocks) ? raw.contentPhase!.blocks : [];

  return {
    template: 'before-after-bridge',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    beforePhase: {
      instruction: raw.beforePhase?.instruction ?? '',
      prompts: beforeWithIds.map((p) => {
        const inputType = p.inputType === 'scale' ? 'scale' : 'text';
        const scaleLabels: [string, string] | undefined =
          inputType === 'scale'
            ? [p.scaleLabels?.[0] ?? 'Low', p.scaleLabels?.[1] ?? 'High']
            : undefined;
        return {
          id: p.id,
          beforeText: p.beforeText ?? '',
          inputType,
          ...(scaleLabels ? { scaleLabels } : {}),
          ...(p.maxLength !== undefined ? { maxLength: p.maxLength } : { maxLength: 300 }),
        };
      }),
    },
    contentPhase: {
      instruction: raw.contentPhase?.instruction ?? '',
      blocks: blocks.map((b) => ({
        id: b.id || crypto.randomUUID(),
        type: b.type === 'data' || b.type === 'quote' ? b.type : 'text',
        content: b.content ?? '',
        ...(b.source ? { source: b.source } : {}),
      })),
    },
    afterPhase: {
      instruction: raw.afterPhase?.instruction ?? '',
      prompts: afterWithIds.map((p) => ({ id: p.id, afterText: p.afterText ?? '' })),
    },
    comparison: { reflectionPrompt: raw.comparison?.reflectionPrompt ?? '' },
  };
}

function emptyBeforeAfterBridge(): BeforeAfterBridgeSlideData {
  return {
    template: 'before-after-bridge',
    title: '',
    subtitle: '',
    beforePhase: { instruction: '', prompts: [] },
    contentPhase: { instruction: '', blocks: [] },
    afterPhase: { instruction: '', prompts: [] },
    comparison: { reflectionPrompt: '' },
  };
}

/**
 * Importer for spot-the-error. Generates UUIDs for missing section + error
 * ids, ensures every "error" chunk has an explanation, and warns on empty
 * sections (no error chunks at all → unwinnable section).
 */
export function buildSpotTheError(
  ds: DesignerSlide,
  warnings: string[],
): SpotTheErrorSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<SpotTheErrorSlideData> | undefined;
  if (!raw || !Array.isArray(raw.sections)) {
    warnings.push('spot-the-error: missing or malformed `sections` array');
    return {
      template: 'spot-the-error',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      sections: [],
    };
  }

  return {
    template: 'spot-the-error',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    sections: raw.sections.map((section, sIdx) => {
      const sectionId = section.id || crypto.randomUUID();
      const content = Array.isArray(section.content) ? section.content : [];
      const errorCount = content.filter((c) => c.type === 'error').length;
      if (errorCount === 0) {
        warnings.push(`spot-the-error: section ${sIdx + 1} has no error chunks`);
      }
      return {
        id: sectionId,
        instruction: section.instruction ?? '',
        context: section.context ?? '',
        content: content.map((chunk, cIdx) => {
          if (chunk.type === 'error') {
            if (!chunk.explanation || !chunk.explanation.trim()) {
              warnings.push(
                `spot-the-error: section ${sIdx + 1} error chunk ${cIdx + 1} has no explanation`,
              );
            }
            return {
              type: 'error' as const,
              text: chunk.text ?? '',
              errorId: chunk.errorId || crypto.randomUUID(),
              explanation: chunk.explanation ?? '',
            };
          }
          if (chunk.type === 'decoy') {
            return { type: 'decoy' as const, text: chunk.text ?? '' };
          }
          return { type: 'text' as const, text: chunk.text ?? '' };
        }),
        completionMessage: section.completionMessage ?? '',
      };
    }),
  };
}

/**
 * Importer for claim-evidence. Generates UUIDs for missing evidence ids,
 * enforces argument rules sanity, warns on missing expert reasoning or
 * empty evidencePool, fills missing expert model.
 */
export function buildClaimEvidence(
  ds: DesignerSlide,
  warnings: string[],
): ClaimEvidenceSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ClaimEvidenceSlideData> | undefined;
  if (!raw || !Array.isArray(raw.evidencePool)) {
    warnings.push('claim-evidence: missing or malformed data');
    return {
      template: 'claim-evidence',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      claim: { statement: '' },
      evidencePool: [],
      argumentRules: { minEvidence: 1, maxEvidence: 5, requireReasoning: true },
      expertModel: { selectedEvidence: [], reasoning: '' },
      reflection: { prompt: '' },
    };
  }

  const evidencePool = raw.evidencePool.map((ev) => {
    if (!ev.strength || !['strong', 'partial', 'distractor'].includes(ev.strength)) {
      warnings.push(`claim-evidence: evidence "${ev.label ?? ev.id ?? '?'}" missing or invalid strength`);
    }
    return {
      id: ev.id || crypto.randomUUID(),
      label: ev.label ?? '',
      detail: ev.detail ?? '',
      strength: (ev.strength as 'strong' | 'partial' | 'distractor') ?? 'partial',
      ...(ev.expertReasoning ? { expertReasoning: ev.expertReasoning } : {}),
    };
  });

  if (evidencePool.filter((e) => e.strength === 'distractor').length < 2) {
    warnings.push('claim-evidence: fewer than 2 distractors — discrimination signal will be weak');
  }

  const rules = raw.argumentRules ?? { minEvidence: 1, maxEvidence: 5, requireReasoning: true };
  if (rules.minEvidence > rules.maxEvidence) {
    warnings.push('claim-evidence: minEvidence > maxEvidence');
  }

  const evidenceIds = new Set(evidencePool.map((e) => e.id));
  const expertSelected = (raw.expertModel?.selectedEvidence ?? []).filter((id) => evidenceIds.has(id));
  if (expertSelected.length === 0) {
    warnings.push('claim-evidence: expertModel.selectedEvidence is empty — no answer key');
  }

  return {
    template: 'claim-evidence',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    claim: { statement: raw.claim?.statement ?? '', ...(raw.claim?.context ? { context: raw.claim.context } : {}) },
    evidencePool,
    argumentRules: rules,
    ...(raw.counterClaim
      ? {
          counterClaim: {
            statement: raw.counterClaim.statement ?? '',
            strongEvidence: (raw.counterClaim.strongEvidence ?? []).filter((id) => evidenceIds.has(id)),
          },
        }
      : {}),
    expertModel: {
      selectedEvidence: expertSelected,
      reasoning: raw.expertModel?.reasoning ?? '',
    },
    reflection: { prompt: raw.reflection?.prompt ?? '' },
  };
}

/**
 * Importer for gallery-walk. Generates UUIDs for missing station and content
 * block ids, warns on stations with no content blocks or missing reflection
 * prompts, and normalises content block types to text/image/quote.
 */
export function buildGalleryWalk(
  ds: DesignerSlide,
  warnings: string[],
): GalleryWalkSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<GalleryWalkSlideData> | undefined;
  if (!raw || !Array.isArray(raw.stations)) {
    warnings.push('gallery-walk: missing or malformed `stations` array');
    return {
      template: 'gallery-walk',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      intro: raw?.intro ?? '',
      stations: [],
      synthesis: { prompt: raw?.synthesis?.prompt ?? '' },
    };
  }

  return {
    template: 'gallery-walk',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    intro: raw.intro ?? '',
    stations: raw.stations.map((s, idx) => {
      const content = Array.isArray(s.content) ? s.content : [];
      if (content.length === 0) {
        warnings.push(`gallery-walk: station ${idx + 1} has no content blocks`);
      }
      if (!s.reflectionPrompt || !s.reflectionPrompt.trim()) {
        warnings.push(`gallery-walk: station ${idx + 1} has no reflection prompt`);
      }
      return {
        id: s.id || crypto.randomUUID(),
        title: s.title ?? '',
        teaser: s.teaser ?? '',
        content: content.map((b) => ({
          type: b.type === 'image' || b.type === 'quote' ? b.type : 'text',
          value: b.value ?? '',
          ...(b.attribution ? { attribution: b.attribution } : {}),
        })),
        reflectionPrompt: s.reflectionPrompt ?? '',
      };
    }),
    synthesis: { prompt: raw.synthesis?.prompt ?? '' },
  };
}

const EXHIBIT_KINDS: readonly ScenarioExhibit['kind'][] = [
  'document', 'draft', 'email', 'message', 'report', 'transcript', 'data', 'other',
];

/**
 * Coerce a raw exhibit (the artifact the learner examines — see
 * ScenarioExhibit in slideTypes.ts). Returns undefined when there is no body:
 * an exhibit without its text is exactly the "mentioned but never shown"
 * failure the field exists to prevent, so an empty one must not render a
 * hollow frame.
 */
function coerceExhibit(raw: unknown): ScenarioExhibit | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const e = raw as Partial<ScenarioExhibit>;
  const body = typeof e.body === 'string' ? e.body.trim() : '';
  if (!body) return undefined;
  return {
    kind: EXHIBIT_KINDS.includes(e.kind as ScenarioExhibit['kind'])
      ? (e.kind as ScenarioExhibit['kind'])
      : 'document',
    title: typeof e.title === 'string' ? e.title : '',
    body,
    ...(typeof e.attribution === 'string' && e.attribution ? { attribution: e.attribution } : {}),
  };
}

/**
 * Importer for perspective-lenses. Generates UUIDs for missing lens + sub-prompt
 * ids; warns on empty lenses, missing prompts, lenses with no sub-prompts.
 */
export function buildPerspectiveLenses(
  ds: DesignerSlide,
  warnings: string[],
): PerspectiveLensesSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<PerspectiveLensesSlideData> | undefined;
  if (!raw || !Array.isArray(raw.lenses)) {
    warnings.push('perspective-lenses: missing or malformed `lenses` array');
    return {
      template: 'perspective-lenses',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      stimulus: { context: raw?.stimulus?.context ?? '' },
      lenses: [],
      synthesis: { prompt: raw?.synthesis?.prompt ?? '', showLensComparison: raw?.synthesis?.showLensComparison ?? true },
    };
  }
  if (raw.lenses.length < 2) {
    warnings.push('perspective-lenses: fewer than 2 lenses — no contrast to draw');
  }
  const lensExhibit = coerceExhibit(raw.exhibit);
  return {
    template: 'perspective-lenses',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    stimulus: { context: raw.stimulus?.context ?? '', ...(raw.stimulus?.imageAlt ? { imageAlt: raw.stimulus.imageAlt } : {}) },
    ...(lensExhibit ? { exhibit: lensExhibit } : {}),
    lenses: raw.lenses.map((lens, lIdx) => {
      const subPrompts = Array.isArray(lens.subPrompts) ? lens.subPrompts : [];
      if (subPrompts.length === 0) {
        warnings.push(`perspective-lenses: lens ${lIdx + 1} (${lens.label ?? '?'}) has no sub-prompts`);
      }
      return {
        id: lens.id || crypto.randomUUID(),
        label: lens.label ?? '',
        iconKey: lens.iconKey ?? 'default',
        colour: lens.colour ?? '#666',
        prompt: lens.prompt ?? '',
        subPrompts: subPrompts.map((p) => ({
          id: p.id || crypto.randomUUID(),
          text: p.text ?? '',
          inputType: p.inputType === 'select' ? 'select' : 'text',
          ...(p.inputType === 'select' ? { options: Array.isArray(p.options) ? p.options : [] } : {}),
          ...(p.maxLength !== undefined ? { maxLength: p.maxLength } : {}),
        })),
      };
    }),
    synthesis: {
      prompt: raw.synthesis?.prompt ?? '',
      showLensComparison: raw.synthesis?.showLensComparison ?? true,
    },
  };
}

/**
 * Importer for scaffolded-investigation. Enforces 3 rounds with escalating
 * scaffolding; warns on missing preset probes, invalid mode names.
 */
export function buildScaffoldedInvestigation(
  ds: DesignerSlide,
  warnings: string[],
): ScaffoldedInvestigationSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ScaffoldedInvestigationSlideData> | undefined;
  if (!raw || !Array.isArray(raw.rounds)) {
    warnings.push('scaffolded-investigation: missing or malformed `rounds` array');
    return {
      template: 'scaffolded-investigation',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      rounds: [],
      debrief: { prompt: raw?.debrief?.prompt ?? '' },
    };
  }
  if (raw.rounds.length !== 3) {
    warnings.push(`scaffolded-investigation: expected 3 rounds, got ${raw.rounds.length}`);
  }
  return {
    template: 'scaffolded-investigation',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    rounds: raw.rounds.map((r, idx) => {
      const validModes = ['guided', 'semi-guided', 'independent'];
      const mode = validModes.includes(r.mode) ? r.mode : (['guided', 'semi-guided', 'independent'] as const)[Math.min(idx, 2)];
      if (!validModes.includes(r.mode)) {
        warnings.push(`scaffolded-investigation: round ${idx + 1} has invalid mode "${r.mode}"; defaulted to "${mode}"`);
      }
      return {
        id: r.id || crypto.randomUUID(),
        roundNumber: (idx + 1) as 1 | 2 | 3,
        mode: mode as 'guided' | 'semi-guided' | 'independent',
        title: r.title ?? '',
        content: r.content ?? '',
        readingPauseSeconds: r.readingPauseSeconds ?? 0,
        presetProbes: Array.isArray(r.presetProbes)
          ? r.presetProbes.map((p) => ({ tag: p.tag ?? '', question: p.question ?? '', response: p.response ?? '' }))
          : [],
        customProbesRequired: r.customProbesRequired ?? 0,
      };
    }),
    debrief: { prompt: raw.debrief?.prompt ?? '' },
  };
}

/**
 * Importer for layered-reveal. Generates UUIDs for missing layer + assessment
 * prompt ids; warns on missing scenario context, empty layers, missing
 * synthesis reveal.
 */
export function buildLayeredReveal(
  ds: DesignerSlide,
  warnings: string[],
): LayeredRevealSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<LayeredRevealSlideData> | undefined;
  if (!raw || !Array.isArray(raw.layers)) {
    warnings.push('layered-reveal: missing or malformed `layers` array');
    return {
      template: 'layered-reveal',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      scenario: { context: raw?.scenario?.context ?? '' },
      layers: [],
      synthesis: { revealText: raw?.synthesis?.revealText ?? '', reflectionPrompt: raw?.synthesis?.reflectionPrompt ?? '', showAssessmentTrail: true },
    };
  }
  if (!raw.scenario?.context) {
    warnings.push('layered-reveal: missing scenario context');
  }
  if (!raw.synthesis?.revealText) {
    warnings.push('layered-reveal: missing synthesis reveal text — the final reframe is the point');
  }
  return {
    template: 'layered-reveal',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    scenario: { context: raw.scenario?.context ?? '' },
    layers: raw.layers.map((l, idx) => {
      const validTypes = ['text', 'data', 'quote', 'image-description'] as const;
      const contentType: (typeof validTypes)[number] = validTypes.includes(l.contentType as (typeof validTypes)[number])
        ? (l.contentType as (typeof validTypes)[number])
        : 'text';
      const prompts = Array.isArray(l.assessmentPrompts) ? l.assessmentPrompts : [];
      if (prompts.length === 0) {
        warnings.push(`layered-reveal: layer ${idx + 1} has no assessment prompts`);
      }
      return {
        id: l.id || crypto.randomUUID(),
        label: l.label ?? '',
        content: l.content ?? '',
        contentType,
        assessmentPrompts: prompts.map((p) => {
          const inputType: 'text' | 'select' | 'scale' =
            p.inputType === 'select' || p.inputType === 'scale' ? p.inputType : 'text';
          return {
            id: p.id || crypto.randomUUID(),
            text: p.text ?? '',
            inputType,
            ...(inputType === 'select' ? { options: Array.isArray(p.options) ? p.options : [] } : {}),
            ...(inputType === 'scale' ? { scaleLabels: (Array.isArray(p.scaleLabels) && p.scaleLabels.length === 2 ? p.scaleLabels : ['Low', 'High']) as [string, string] } : {}),
            ...(p.maxLength !== undefined ? { maxLength: p.maxLength } : {}),
          };
        }),
      };
    }),
    synthesis: {
      revealText: raw.synthesis?.revealText ?? '',
      reflectionPrompt: raw.synthesis?.reflectionPrompt ?? '',
      showAssessmentTrail: raw.synthesis?.showAssessmentTrail ?? true,
    },
  };
}

/**
 * Importer for timeline-navigator. UUIDs for missing event ids; warns on
 * missing date/title/summary, no milestones, fewer than 3 events.
 */
export function buildTimelineNavigator(
  ds: DesignerSlide,
  warnings: string[],
): TimelineNavigatorSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<TimelineNavigatorSlideData> | undefined;
  if (!raw || !Array.isArray(raw.events)) {
    warnings.push('timeline-navigator: missing or malformed `events` array');
    return {
      template: 'timeline-navigator',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      events: [],
      reflection: { prompt: raw?.reflection?.prompt ?? '' },
    };
  }
  if (raw.events.length < 3) warnings.push('timeline-navigator: timeline has fewer than 3 events');
  if (!raw.events.some((e) => e.isMilestone)) {
    warnings.push('timeline-navigator: no events marked as milestone');
  }
  return {
    template: 'timeline-navigator',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    events: raw.events.map((e, idx) => {
      if (!e.date) warnings.push(`timeline-navigator: event ${idx + 1} has no date`);
      if (!e.title) warnings.push(`timeline-navigator: event ${idx + 1} has no title`);
      return {
        id: e.id || crypto.randomUUID(),
        date: e.date ?? '',
        title: e.title ?? '',
        summary: e.summary ?? '',
        detail: e.detail ?? '',
        isMilestone: Boolean(e.isMilestone),
        ...(e.question ? { question: e.question } : {}),
      };
    }),
    reflection: { prompt: raw.reflection?.prompt ?? '' },
  };
}

/**
 * Importer for progressive-builder. UUIDs for missing layer ids; warns on
 * empty baseState, fewer than 2 layers, missing transformed outputs.
 */
export function buildProgressiveBuilder(
  ds: DesignerSlide,
  warnings: string[],
): ProgressiveBuilderSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ProgressiveBuilderSlideData> | undefined;
  if (!raw || !Array.isArray(raw.layers)) {
    warnings.push('progressive-builder: missing or malformed `layers` array');
    return {
      template: 'progressive-builder',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      baseState: raw?.baseState ?? '',
      layers: [],
      reflection: { prompt: raw?.reflection?.prompt ?? '' },
    };
  }
  if (!raw.baseState) warnings.push('progressive-builder: missing baseState (starting point)');
  if (raw.layers.length < 2) warnings.push('progressive-builder: fewer than 2 layers — no progression to show');
  return {
    template: 'progressive-builder',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    baseState: raw.baseState ?? '',
    layers: raw.layers.map((l, idx) => {
      if (!l.transformedOutput) {
        warnings.push(`progressive-builder: layer ${idx + 1} has no transformedOutput — the cumulative view will be empty`);
      }
      return {
        id: l.id || crypto.randomUUID(),
        label: l.label ?? '',
        addition: l.addition ?? '',
        transformedOutput: l.transformedOutput ?? '',
      };
    }),
    reflection: { prompt: raw.reflection?.prompt ?? '' },
  };
}

/**
 * Importer for guided-sandbox. UUIDs for missing dimension ids; warns on
 * empty task, missing keywords (zero scoring possible), minScore out of range.
 */
export function buildGuidedSandbox(
  ds: DesignerSlide,
  warnings: string[],
): GuidedSandboxSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<GuidedSandboxSlideData> | undefined;
  if (!raw || !Array.isArray(raw.dimensions)) {
    warnings.push('guided-sandbox: missing or malformed `dimensions` array');
    return {
      template: 'guided-sandbox',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      task: raw?.task ?? '',
      placeholder: raw?.placeholder ?? '',
      minScore: raw?.minScore ?? 50,
      dimensions: [],
      guidance: raw?.guidance ?? {},
    };
  }
  if (!raw.task) warnings.push('guided-sandbox: missing task description');
  if (raw.minScore !== undefined && (raw.minScore < 0 || raw.minScore > 100)) {
    warnings.push(`guided-sandbox: minScore ${raw.minScore} out of range — clamping to [0, 100]`);
  }
  return {
    template: 'guided-sandbox',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    task: raw.task ?? '',
    placeholder: raw.placeholder ?? '',
    minScore: Math.max(0, Math.min(100, raw.minScore ?? 50)),
    dimensions: raw.dimensions.map((d, idx) => {
      const keywords = Array.isArray(d.keywords) ? d.keywords : [];
      if (keywords.length === 0) {
        warnings.push(`guided-sandbox: dimension ${idx + 1} (${d.label ?? '?'}) has no keywords — scoring will always be 0`);
      }
      return {
        id: d.id || crypto.randomUUID(),
        label: d.label ?? '',
        description: d.description ?? '',
        keywords,
        tips: d.tips ?? '',
        weight: d.weight ?? 1,
      };
    }),
    guidance: raw.guidance ?? {},
  };
}

/**
 * Importer for drag-sort. UUIDs for missing item/zone ids; warns on items
 * whose correctZone doesn't reference an existing zone; min-zone count.
 */
export function buildDragSort(ds: DesignerSlide, warnings: string[]): DragSortSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<DragSortSlideData> | undefined;
  if (!raw || !Array.isArray(raw.items) || !Array.isArray(raw.zones)) {
    warnings.push('drag-sort: missing or malformed `items` / `zones`');
    return {
      template: 'drag-sort',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      instruction: raw?.instruction ?? '',
      mode: raw?.mode === 'sequence' ? 'sequence' : 'categorise',
      items: [],
      zones: [],
      feedback: raw?.feedback ?? { allCorrect: '', partial: '' },
    };
  }
  const zones = raw.zones.map((z) => ({
    id: z.id || crypto.randomUUID(),
    label: z.label ?? '',
  }));
  const zoneIds = new Set(zones.map((z) => z.id));
  if (zones.length < 2) warnings.push('drag-sort: fewer than 2 zones — not much sorting to do');
  return {
    template: 'drag-sort',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    instruction: raw.instruction ?? '',
    mode: raw.mode === 'sequence' ? 'sequence' : 'categorise',
    items: raw.items.map((item, idx) => {
      if (!zoneIds.has(item.correctZone)) {
        warnings.push(`drag-sort: item ${idx + 1} (${item.text ?? '?'}) correctZone "${item.correctZone}" not in zones`);
      }
      return {
        id: item.id || crypto.randomUUID(),
        text: item.text ?? '',
        correctZone: item.correctZone ?? zones[0]?.id ?? '',
        ...(item.correctPosition !== undefined ? { correctPosition: item.correctPosition } : {}),
      };
    }),
    zones,
    feedback: {
      allCorrect: raw.feedback?.allCorrect ?? '',
      partial: raw.feedback?.partial ?? '',
      ...(raw.feedback?.perItem ? { perItem: raw.feedback.perItem } : {}),
    },
  };
}

/**
 * Importer for tension-mapper. UUIDs for missing factor ids; warns on factor
 * description over 80 chars (the renderer pinches), missing expert insights
 * for factors, fewer than 4 factors.
 */
export function buildTensionMapper(ds: DesignerSlide, warnings: string[]): TensionMapperSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<TensionMapperSlideData> | undefined;
  if (!raw || !raw.tension || !Array.isArray(raw.factors)) {
    warnings.push('tension-mapper: missing or malformed `tension` / `factors`');
    return {
      template: 'tension-mapper',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      tension: raw?.tension ?? { statement: '', poleA: { label: '', description: '' }, poleB: { label: '', description: '' } },
      factors: [],
      expertAnalysis: raw?.expertAnalysis ?? { summary: '', insights: [] },
      reflection: { prompt: raw?.reflection?.prompt ?? '' },
    };
  }
  if (raw.factors.length < 4) warnings.push('tension-mapper: fewer than 4 factors — not much to map');
  const factors = raw.factors.map((f, idx) => {
    if ((f.description ?? '').length > 80) {
      warnings.push(`tension-mapper: factor ${idx + 1} (${f.label ?? '?'}) description exceeds 80 chars; will pinch in renderer`);
    }
    return {
      id: f.id || crypto.randomUUID(),
      label: f.label ?? '',
      description: f.description ?? '',
    };
  });
  const factorIds = new Set(factors.map((f) => f.id));
  const insights = (raw.expertAnalysis?.insights ?? []).filter((ins) => {
    if (!factorIds.has(ins.factorId)) {
      warnings.push(`tension-mapper: expert insight references unknown factorId "${ins.factorId}" — dropping`);
      return false;
    }
    return true;
  });
  return {
    template: 'tension-mapper',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    tension: raw.tension,
    factors,
    expertAnalysis: { summary: raw.expertAnalysis?.summary ?? '', insights },
    reflection: { prompt: raw.reflection?.prompt ?? '' },
  };
}

/**
 * Importer for scenario-simulation. UUIDs for missing branch ids; warns on
 * missing `start` branch, choices pointing to nonexistent branches, no
 * ending branches reachable.
 */
export function buildScenarioSimulation(ds: DesignerSlide, warnings: string[]): ScenarioSimulationSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ScenarioSimulationSlideData> | undefined;
  if (!raw || !Array.isArray(raw.branches)) {
    warnings.push('scenario-simulation: missing or malformed `branches`');
    return {
      template: 'scenario-simulation',
      title: raw?.title ?? '',
      subtitle: raw?.subtitle ?? '',
      branches: [],
    };
  }
  const branches = raw.branches.map((b) => ({
    ...b,
    id: b.id || crypto.randomUUID(),
    type: b.type === 'ending' ? ('ending' as const) : ('decision' as const),
  }));
  if (!branches.some((b) => b.id === 'start')) {
    warnings.push('scenario-simulation: no branch has id "start" — the renderer will use the first branch as the entry');
  }
  const branchIds = new Set(branches.map((b) => b.id));
  for (const b of branches) {
    if (b.type === 'decision' && Array.isArray(b.choices)) {
      for (const c of b.choices) {
        if (!branchIds.has(c.nextBranch)) {
          warnings.push(`scenario-simulation: branch "${b.id}" choice ${c.letter} → "${c.nextBranch}" which doesn't exist`);
        }
      }
    }
  }
  if (!branches.some((b) => b.type === 'ending')) {
    warnings.push('scenario-simulation: no ending branches — every path leads back into the decision graph');
  }
  const simExhibit = coerceExhibit(raw.exhibit);
  return {
    template: 'scenario-simulation',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    ...(simExhibit ? { exhibit: simExhibit } : {}),
    branches,
  };
}

const TEACH_BACK_KINDS: readonly TeachBackCoachKind[] = [
  'perspective',
  'targeted',
  'ownership',
  'backfire',
  'answer',
];
const TEACH_BACK_ARTIFACT_KINDS = ['email', 'message', 'document', 'code', 'plan'] as const;
const TEACH_BACK_OUTCOMES = ['aha', 'partial', 'backfire', 'dependency'] as const;

/**
 * Importer for teach-back (the learner coaches a struggling student). Thin
 * validator + id generator: mints UUIDs for missing fragment / option / branch
 * ids, coerces enums (coach kind, artifact kind, outcome) into their allowed
 * sets, and warns on dangling references — misconceptionId not among the
 * diagnosisOptions, a branch predicate naming an unknown coach kind, missing
 * backfire/answer fragments, no load-bearing artifact line, or a revision that
 * doesn't carry exactly one residue. See CREATE-COMPOSITION-HANDOVER.md §9.1.
 */
export function buildTeachBack(ds: DesignerSlide, warnings: string[]): TeachBackSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<TeachBackSlideData> | undefined;
  const coerceKind = (k: unknown, ctx: string): TeachBackCoachKind => {
    if (typeof k === 'string' && TEACH_BACK_KINDS.includes(k as TeachBackCoachKind)) {
      return k as TeachBackCoachKind;
    }
    warnings.push(`teach-back: ${ctx} has invalid coach kind "${String(k)}"; defaulted to "targeted"`);
    return 'targeted';
  };
  const coerceArtifactKind = (k: unknown): TeachBackSlideData['artifact']['kind'] =>
    TEACH_BACK_ARTIFACT_KINDS.includes(k as (typeof TEACH_BACK_ARTIFACT_KINDS)[number])
      ? (k as (typeof TEACH_BACK_ARTIFACT_KINDS)[number])
      : 'email';

  const empty = (): TeachBackSlideData => ({
    template: 'teach-back',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    instruction: raw?.instruction ?? '',
    student: {
      name: raw?.student?.name ?? '',
      role: raw?.student?.role ?? '',
      voiceOpening: raw?.student?.voiceOpening ?? '',
    },
    artifact: { kind: coerceArtifactKind(raw?.artifact?.kind), fragments: [] },
    misconceptionId: raw?.misconceptionId ?? '',
    diagnosisOptions: [],
    coachingFragments: [],
    maxCoaching: 3,
    branches: [],
    defaultBranch: {
      outcome: 'partial',
      studentReply: raw?.defaultBranch?.studentReply ?? '',
      coachNote: raw?.defaultBranch?.coachNote ?? '',
    },
    revision: { kind: coerceArtifactKind(raw?.revision?.kind), fragments: [] },
    expertNote: raw?.expertNote ?? '',
    ...(raw?.conclusion ? { conclusion: raw.conclusion } : {}),
  });

  if (!raw || !Array.isArray(raw.diagnosisOptions) || !raw.artifact) {
    warnings.push('teach-back: missing or malformed `artifact` / `diagnosisOptions`');
    return empty();
  }

  // --- Artifact fragments (the flawed work the learner red-pens) ------------
  const artifactFragments = (Array.isArray(raw.artifact.fragments) ? raw.artifact.fragments : []).map(
    (f) => ({
      id: f.id || crypto.randomUUID(),
      text: f.text ?? '',
      strikeable: f.strikeable !== false,
      damage: Boolean(f.damage),
      ...(f.loadBearing ? { loadBearing: true } : {}),
    }),
  );
  if (!artifactFragments.some((f) => f.damage)) {
    warnings.push('teach-back: artifact has no damage fragment — nothing to red-pen');
  }
  if (!artifactFragments.some((f) => f.loadBearing)) {
    warnings.push('teach-back: artifact has no load-bearing line — over-striking cannot be tested');
  }

  // --- Diagnosis options (the belief underneath) ---------------------------
  const diagnosisOptions = raw.diagnosisOptions.map((o) => ({
    id: o.id || crypto.randomUUID(),
    text: o.text ?? '',
    correct: Boolean(o.correct),
    feedback: o.feedback ?? '',
  }));
  const optionIds = new Set(diagnosisOptions.map((o) => o.id));
  // Resolve the correct diagnosis: prefer the authored misconceptionId, else the
  // option flagged `correct`.
  let misconceptionId = raw.misconceptionId ?? '';
  if (!optionIds.has(misconceptionId)) {
    const flagged = diagnosisOptions.find((o) => o.correct);
    if (flagged) {
      warnings.push(
        `teach-back: misconceptionId "${misconceptionId}" is not a diagnosisOption id — using the option flagged correct`,
      );
      misconceptionId = flagged.id;
    } else {
      warnings.push('teach-back: misconceptionId references no diagnosisOption and none is flagged correct');
    }
  }
  if (diagnosisOptions.filter((o) => o.id !== misconceptionId).length < 2) {
    warnings.push('teach-back: fewer than 2 diagnosis distractors — the diagnosis is too easy');
  }

  // --- Coaching fragments (composed into the teaching move) ----------------
  const coachingFragments = (Array.isArray(raw.coachingFragments) ? raw.coachingFragments : []).map(
    (f, i) => ({
      id: f.id || crypto.randomUUID(),
      text: f.text ?? '',
      kind: coerceKind(f.kind, `coachingFragment ${i + 1}`),
    }),
  );
  const presentKinds = new Set(coachingFragments.map((f) => f.kind));
  if (!presentKinds.has('backfire')) {
    warnings.push('teach-back: no backfire coaching fragment — the wrong-move branch is unreachable');
  }
  if (!presentKinds.has('answer')) {
    warnings.push('teach-back: no answer-giving coaching fragment — the dependency branch is unreachable');
  }

  // --- Branches (authored student replies keyed on the composed move) ------
  const checkKinds = (kinds: unknown, ctx: string): TeachBackCoachKind[] | undefined => {
    if (!Array.isArray(kinds)) return undefined;
    return kinds.map((k) => {
      if (typeof k !== 'string' || !TEACH_BACK_KINDS.includes(k as TeachBackCoachKind)) {
        warnings.push(`teach-back: branch "${ctx}" references unknown coach kind "${String(k)}"`);
        return 'targeted' as TeachBackCoachKind;
      }
      return k as TeachBackCoachKind;
    });
  };
  const branches = (Array.isArray(raw.branches) ? raw.branches : []).map((b) => {
    const id = b.id || crypto.randomUUID();
    const when = b.when ?? {};
    const outcome = TEACH_BACK_OUTCOMES.includes(b.outcome as (typeof TEACH_BACK_OUTCOMES)[number])
      ? (b.outcome as (typeof TEACH_BACK_OUTCOMES)[number])
      : 'partial';
    if (!TEACH_BACK_OUTCOMES.includes(b.outcome as (typeof TEACH_BACK_OUTCOMES)[number])) {
      warnings.push(`teach-back: branch "${id}" has invalid outcome "${String(b.outcome)}"; defaulted to "partial"`);
    }
    return {
      id,
      when: {
        ...(when.opensWithKind ? { opensWithKind: coerceKind(when.opensWithKind, `branch "${id}".opensWithKind`) } : {}),
        ...(when.includesAnyKind ? { includesAnyKind: checkKinds(when.includesAnyKind, `${id}.includesAnyKind`) } : {}),
        ...(when.excludesAllKinds ? { excludesAllKinds: checkKinds(when.excludesAllKinds, `${id}.excludesAllKinds`) } : {}),
      },
      outcome,
      studentReply: b.studentReply ?? '',
      coachNote: b.coachNote ?? '',
      ...(b.allowsAdapt ? { allowsAdapt: true } : {}),
    };
  });
  if (branches.length === 0) {
    warnings.push('teach-back: no branches — every move falls through to defaultBranch');
  }

  // --- Revision (beat 5 — the rewrite carrying one residue) ----------------
  const revisionFragments = (Array.isArray(raw.revision?.fragments) ? raw.revision!.fragments : []).map(
    (f) => ({
      id: f.id || crypto.randomUUID(),
      text: f.text ?? '',
      strikeable: f.strikeable !== false,
      isResidue: Boolean(f.isResidue),
    }),
  );
  const residueCount = revisionFragments.filter((f) => f.isResidue).length;
  if (residueCount !== 1) {
    warnings.push(`teach-back: revision must have exactly 1 residue fragment (found ${residueCount})`);
  }

  // --- Optional adapt beat (after a backfire / dependency outcome) ----------
  let adaptBeat: TeachBackSlideData['adaptBeat'];
  if (raw.adaptBeat && typeof raw.adaptBeat === 'object') {
    const options = (Array.isArray(raw.adaptBeat.options) ? raw.adaptBeat.options : []).map((o, i) => ({
      id: o.id || crypto.randomUUID(),
      text: o.text ?? '',
      kind: coerceKind(o.kind, `adaptBeat option ${i + 1}`),
      leadsTo: o.leadsTo === 'aha' ? ('aha' as const) : ('unresolved' as const),
      studentReply: o.studentReply ?? '',
      coachNote: o.coachNote ?? '',
    }));
    adaptBeat = { prompt: raw.adaptBeat.prompt ?? '', options };
  }

  const maxCoaching = typeof raw.maxCoaching === 'number' ? Math.max(1, Math.min(3, raw.maxCoaching)) : 3;

  return {
    template: 'teach-back',
    title: raw.title ?? '',
    subtitle: raw.subtitle ?? '',
    instruction: raw.instruction ?? '',
    student: {
      name: raw.student?.name ?? '',
      role: raw.student?.role ?? '',
      voiceOpening: raw.student?.voiceOpening ?? '',
    },
    artifact: {
      kind: coerceArtifactKind(raw.artifact.kind),
      ...(Array.isArray(raw.artifact.header) ? { header: raw.artifact.header } : {}),
      fragments: artifactFragments,
    },
    misconceptionId,
    diagnosisOptions,
    coachingFragments,
    maxCoaching,
    branches,
    defaultBranch: {
      outcome: 'partial',
      studentReply: raw.defaultBranch?.studentReply ?? '',
      coachNote: raw.defaultBranch?.coachNote ?? '',
    },
    ...(adaptBeat ? { adaptBeat } : {}),
    revision: {
      kind: coerceArtifactKind(raw.revision?.kind),
      ...(Array.isArray(raw.revision?.header) ? { header: raw.revision!.header } : {}),
      fragments: revisionFragments,
    },
    expertNote: raw.expertNote ?? '',
    ...(raw.conclusion ? { conclusion: raw.conclusion } : {}),
  };
}

/**
 * Hotspot Explorer importer. The 17th interaction type — restored from
 * Dylan's original Kula Create catalogue. Warns on missing image, alt text,
 * out-of-range coordinates, fewer than 3 hotspots, and incomplete synthesis.
 */
export function buildHotspotExplorer(
  ds: DesignerSlide,
  warnings: string[],
): HotspotExplorerSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<HotspotExplorerSlideData> | undefined;
  const hotspots = Array.isArray(raw?.hotspots) ? raw!.hotspots! : [];

  if (!raw?.imageUrl) warnings.push('hotspot-explorer: missing imageUrl');
  if (!raw?.imageAlt) warnings.push('hotspot-explorer: missing imageAlt (required for a11y)');
  if (hotspots.length < 3) {
    warnings.push(`hotspot-explorer: ${hotspots.length} hotspots — fewer than 3 feels sparse`);
  }
  if (hotspots.length > 8) {
    warnings.push(`hotspot-explorer: ${hotspots.length} hotspots — more than 8 overwhelms`);
  }
  if (!raw?.synthesis?.body || !raw.synthesis?.reflectionPrompt) {
    warnings.push('hotspot-explorer: synthesis body or reflection prompt missing');
  }

  return {
    template: 'hotspot-explorer',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    imageUrl: raw?.imageUrl ?? '',
    imageAlt: raw?.imageAlt ?? '',
    ...(raw?.imageCaption ? { imageCaption: raw.imageCaption } : {}),
    hotspots: hotspots.map((h, idx) => {
      const x = typeof h.x === 'number' ? h.x : 50;
      const y = typeof h.y === 'number' ? h.y : 50;
      if (x < 0 || x > 100 || y < 0 || y > 100) {
        warnings.push(`hotspot-explorer: hotspot ${idx + 1} coords out of 0-100 range`);
      }
      if (!h.label?.trim()) warnings.push(`hotspot-explorer: hotspot ${idx + 1} missing label`);
      if (!h.body?.trim()) warnings.push(`hotspot-explorer: hotspot ${idx + 1} has no body`);
      return {
        id: h.id || crypto.randomUUID(),
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y)),
        label: h.label ?? '',
        title: h.title ?? '',
        body: h.body ?? '',
        ...(h.contentType ? { contentType: h.contentType } : {}),
      };
    }),
    synthesis: {
      title: raw?.synthesis?.title ?? '',
      body: raw?.synthesis?.body ?? '',
      reflectionPrompt: raw?.synthesis?.reflectionPrompt ?? '',
    },
  };
}

const SOURCE_EXAM_SKILLS = ['extraction', 'concept', 'interpretation', 'analysis', 'reliability', 'comparison', 'synthesis'] as const;
const COGNITIVE_LEVELS = ['L1_recall', 'L2_interpret', 'L3_apply'] as const;

/**
 * Importer for source-examination. The Designer emits the SlideData shape
 * directly (one source + a question ladder). Thin validator + id generator:
 * fills missing question ids, clamps `correct` indices into option range, and
 * warns on the content-gate misses (too few levels, no distractors, missing
 * feedback). See REVISION_TEMPLATE_PLAN.md.
 */
export function buildSourceExamination(
  ds: DesignerSlide,
  warnings: string[],
): SourceExaminationSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<SourceExaminationSlideData> | undefined;
  const rawQuestions = Array.isArray(raw?.questions) ? raw!.questions! : [];
  const src = raw?.source ?? { label: '', type: '', attribution: '' };

  if (!src.body && !src.imageUrl) warnings.push('source-examination: source has neither body nor imageUrl');
  if (src.imageUrl && !src.imageAlt) warnings.push('source-examination: imageUrl set but imageAlt missing (a11y)');
  if (rawQuestions.length === 0) warnings.push('source-examination: no questions');

  const questions: SourceExaminationQuestion[] = rawQuestions.map((q, i) => {
    const options = Array.isArray(q.options) ? q.options : [];
    const skill = SOURCE_EXAM_SKILLS.includes(q.skill as never) ? q.skill! : 'extraction';
    if (!SOURCE_EXAM_SKILLS.includes(q.skill as never)) {
      warnings.push(`source-examination: question ${i + 1} has invalid skill "${q.skill}" — defaulted to extraction`);
    }
    const cognitiveLevel = COGNITIVE_LEVELS.includes(q.cognitiveLevel as never) ? q.cognitiveLevel! : 'L1_recall';
    const clamp = (n: number) => Math.max(0, Math.min(options.length - 1, n));
    const correct = Array.isArray(q.correct) ? q.correct.map(clamp) : clamp(typeof q.correct === 'number' ? q.correct : 0);
    if (options.length < 2) warnings.push(`source-examination: question ${i + 1} has fewer than 2 options`);
    if (!q.feedback?.trim()) warnings.push(`source-examination: question ${i + 1} missing feedback (the load-bearing "why")`);
    return {
      id: q.id || `q${i + 1}`,
      number: q.number ?? String(i + 1),
      skill,
      cognitiveLevel,
      marks: typeof q.marks === 'number' ? q.marks : 1,
      ...(typeof q.recommendedTimeSec === 'number' ? { recommendedTimeSec: q.recommendedTimeSec } : {}),
      prompt: q.prompt ?? '',
      ...(q.hint ? { hint: q.hint } : {}),
      options,
      correct,
      ...(typeof q.multiSelectMax === 'number' && q.multiSelectMax > 1 ? { multiSelectMax: q.multiSelectMax } : {}),
      feedback: q.feedback ?? '',
    };
  });

  const levels = new Set(questions.map((q) => q.cognitiveLevel));
  if (levels.size < 2) warnings.push('source-examination: questions span fewer than 2 cognitive levels — the L1→L3 ladder is the point');

  return {
    template: 'source-examination',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    ...(raw?.timed === false ? { timed: false } : {}),
    source: {
      label: src.label ?? '',
      type: src.type ?? '',
      ...(src.body ? { body: src.body } : {}),
      ...(src.imageUrl ? { imageUrl: src.imageUrl } : {}),
      ...(src.imageAlt ? { imageAlt: src.imageAlt } : {}),
      attribution: src.attribution ?? '',
    },
    questions,
    ...(raw?.conclusion ? { conclusion: raw.conclusion } : {}),
  };
}

const MTA_VERDICTS = ['yes', 'partial', 'no'] as const;

/**
 * Importer for mark-this-answer (the evaluative revision companion). Thin
 * validator + id generator: fills missing ids and clamps each criterion's
 * `correct` verdict into the allowed set. See REVISION_TEMPLATE_PLAN.md §3.
 */
export function buildMarkThisAnswer(
  ds: DesignerSlide,
  warnings: string[],
): MarkThisAnswerSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<MarkThisAnswerSlideData> | undefined;
  const rawAnswers = Array.isArray(raw?.answers) ? raw!.answers! : [];
  if (rawAnswers.length === 0) warnings.push('mark-this-answer: no sample answers');

  const answers: MarkThisAnswerSampleAnswer[] = rawAnswers.map((a, i) => {
    const criteria = (Array.isArray(a.criteria) ? a.criteria : []).map((c, j) => {
      const correct = MTA_VERDICTS.includes(c.correct as never) ? c.correct! : 'partial';
      if (!MTA_VERDICTS.includes(c.correct as never)) {
        warnings.push(`mark-this-answer: answer ${i + 1} criterion ${j + 1} has invalid verdict "${c.correct}" — defaulted to partial`);
      }
      if (!c.explanation?.trim()) {
        warnings.push(`mark-this-answer: answer ${i + 1} criterion ${j + 1} missing explanation`);
      }
      return { id: c.id || `c${j + 1}`, prompt: c.prompt ?? '', correct, explanation: c.explanation ?? '' };
    });
    if (criteria.length === 0) warnings.push(`mark-this-answer: answer ${i + 1} has no criteria`);
    if (!a.text?.trim()) warnings.push(`mark-this-answer: answer ${i + 1} has no answer text`);
    return {
      id: a.id || `a${i + 1}`,
      label: a.label ?? `Answer ${String.fromCharCode(65 + i)}`,
      text: a.text ?? '',
      ...(a.approxBand ? { approxBand: a.approxBand } : {}),
      criteria,
    };
  });

  if (!raw?.task?.trim()) warnings.push('mark-this-answer: missing task (the question the answers respond to)');

  return {
    template: 'mark-this-answer',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    task: raw?.task ?? '',
    ...(raw?.rubricNote ? { rubricNote: raw.rubricNote } : {}),
    answers,
    ...(raw?.conclusion ? { conclusion: raw.conclusion } : {}),
  };
}

/**
 * Importer for revision-exam — the SINGLE self-contained revision interactive.
 * Takes the generator's whole-module shape ({ slides: [{source, questions}],
 * markThisAnswer? }) and assembles it into ONE slide: a flat source list + a
 * continuous question list (each question tagged with its source key) + the
 * optional bonus. Reuses buildSourceExamination / buildMarkThisAnswer for the
 * per-part validation so the normalisation stays in one place.
 */
export function buildRevisionExam(ds: DesignerSlide, warnings: string[]): RevisionExamSlideData {
  const raw = (ds as { data?: unknown }).data as
    | { slides?: unknown[]; markThisAnswer?: unknown; paragraphBuilder?: unknown; title?: string; subtitle?: string; timed?: boolean }
    | undefined;
  const rawSlides = Array.isArray(raw?.slides) ? raw!.slides! : [];
  if (rawSlides.length === 0) warnings.push('revision-exam: no source slides');

  const sources: RevisionExamSource[] = [];
  const questions: RevisionExamQuestion[] = [];

  rawSlides.forEach((rawSlide, i) => {
    const se = buildSourceExamination(
      { data: rawSlide } as unknown as Parameters<typeof buildSourceExamination>[0],
      warnings,
    );
    const key = `s${i + 1}`;
    sources.push({
      key,
      label: se.source.label || `Source ${i + 1}`,
      type: se.source.type,
      ...(se.source.body ? { body: se.source.body } : {}),
      ...(se.source.imageUrl ? { imageUrl: se.source.imageUrl } : {}),
      ...(se.source.imageAlt ? { imageAlt: se.source.imageAlt } : {}),
      attribution: se.source.attribution,
    });
    se.questions.forEach((q: SourceExaminationQuestion, qi) => {
      questions.push({
        id: `${key}-${q.id || `q${qi + 1}`}`,
        sourceKey: key,
        number: q.number,
        skill: q.skill,
        cognitiveLevel: q.cognitiveLevel,
        marks: q.marks,
        ...(typeof q.recommendedTimeSec === 'number' ? { recommendedTimeSec: q.recommendedTimeSec } : {}),
        prompt: q.prompt,
        ...(q.hint ? { hint: q.hint } : {}),
        options: q.options,
        correct: q.correct,
        ...(typeof q.multiSelectMax === 'number' ? { multiSelectMax: q.multiSelectMax } : {}),
        feedback: q.feedback,
      });
    });
  });

  const levels = new Set(questions.map((q) => q.cognitiveLevel));
  if (questions.length > 0 && levels.size < 2) {
    warnings.push('revision-exam: questions span fewer than 2 cognitive levels — the L1→L3 ladder is the point');
  }

  // Guided 8-mark paragraph builder (synthesis) — four pick-from-choices steps.
  let paragraphBuilder: RevisionExamSlideData['paragraphBuilder'];
  if (raw?.paragraphBuilder && typeof raw.paragraphBuilder === 'object') {
    const pb = raw.paragraphBuilder as { task?: string; intro?: string; steps?: unknown[] };
    const rawSteps = Array.isArray(pb.steps) ? pb.steps : [];
    if (rawSteps.length === 0) {
      warnings.push('revision-exam: paragraphBuilder present but has no steps — dropped');
    } else {
      const steps = rawSteps.map((s, i) => {
        const stepObj = (s ?? {}) as {
          id?: string; label?: string; prompt?: string; options?: unknown; correct?: unknown; marks?: unknown; feedback?: string;
        };
        const options = Array.isArray(stepObj.options) ? (stepObj.options as string[]) : [];
        if (options.length < 2) warnings.push(`revision-exam: paragraph step ${i + 1} has fewer than 2 options`);
        if (!stepObj.feedback?.trim()) warnings.push(`revision-exam: paragraph step ${i + 1} missing feedback`);
        const correctRaw = typeof stepObj.correct === 'number' ? stepObj.correct : 0;
        const correct = Math.max(0, Math.min(options.length - 1, correctRaw));
        const FALLBACK_LABELS = ['Topic sentence', 'Source evidence', 'Own knowledge', 'Conclusion'];
        return {
          id: stepObj.id || ['topic', 'evidence', 'knowledge', 'conclusion'][i] || `step${i + 1}`,
          label: stepObj.label || FALLBACK_LABELS[i] || `Step ${i + 1}`,
          prompt: stepObj.prompt ?? '',
          options,
          correct,
          marks: typeof stepObj.marks === 'number' ? stepObj.marks : 2,
          feedback: stepObj.feedback ?? '',
        };
      });
      if (steps.length !== 4) {
        warnings.push(`revision-exam: paragraphBuilder has ${steps.length} steps (expected 4: topic → evidence → knowledge → conclusion)`);
      }
      paragraphBuilder = {
        task: pb.task ?? '',
        ...(pb.intro ? { intro: pb.intro } : {}),
        steps,
      };
      if (!pb.task?.trim()) warnings.push('revision-exam: paragraphBuilder missing task (the paragraph question)');
    }
  }

  let markThisAnswer: RevisionExamSlideData['markThisAnswer'];
  if (raw?.markThisAnswer && typeof raw.markThisAnswer === 'object') {
    const mta = buildMarkThisAnswer(
      { data: raw.markThisAnswer } as unknown as Parameters<typeof buildMarkThisAnswer>[0],
      warnings,
    );
    if (mta.answers.length > 0) {
      markThisAnswer = {
        task: mta.task,
        ...(mta.rubricNote ? { rubricNote: mta.rubricNote } : {}),
        answers: mta.answers as MarkThisAnswerSampleAnswer[],
      };
    }
  }

  const firstSlide = rawSlides[0] as { data?: { title?: string }; title?: string } | undefined;
  const title =
    raw?.title ??
    (firstSlide && typeof firstSlide === 'object' && 'title' in firstSlide ? (firstSlide as { title?: string }).title : undefined) ??
    'Revision exam';

  return {
    template: 'revision-exam',
    title: title ?? 'Revision exam',
    subtitle: raw?.subtitle ?? '',
    ...(raw?.timed === false ? { timed: false } : {}),
    sources,
    questions,
    ...(paragraphBuilder ? { paragraphBuilder } : {}),
    ...(markThisAnswer ? { markThisAnswer } : {}),
  };
}

/**
 * Bridge slide importer. Bridges are emitted by the compositor stage (not the
 * single-template Builder). Always thin — the agent gives us narrativeText and
 * a recap toggle, we pass through. priorSlideId is resolved client-side from
 * group position at render time, not stored in the agent output.
 */
export function buildBridge(ds: DesignerSlide, warnings: string[]): BridgeSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<BridgeSlideData> | undefined;
  if (!raw) {
    warnings.push('bridge: missing data — falling back to empty narrative');
  }
  return {
    template: 'bridge',
    narrativeText: typeof raw?.narrativeText === 'string' ? raw.narrativeText : '',
    showResponseRecap: raw?.showResponseRecap !== false, // default on
  };
}

/**
 * Importer for the Phase C `interactive-slot` template. Receives a Designer
 * card whose `label === 'INTERACTIVE'` and emits an `InteractiveSlotSlideData`
 * with the card's text as the brief. Optional anchor verb is read from
 * `note`. Phase E's Create walkthrough later replaces these slots with built
 * scenario groups.
 */
export function buildInteractiveSlot(
  ds: DesignerSlide,
  _warnings: string[],
): import('../../components/course-builder/slides/slideTypes').InteractiveSlotSlideData {
  // Note field may carry "<verb>" or "<verb> · <position>" — split if present.
  const rawNote = ((ds as { note?: string }).note || '').trim();
  let anchorVerb: string | undefined;
  let position: 'opener' | 'core' | 'closer' | undefined;
  if (rawNote) {
    const parts = rawNote.split('·').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 0) anchorVerb = parts[0] || undefined;
    const candidatePos = parts[1]?.toLowerCase();
    if (candidatePos === 'opener' || candidatePos === 'core' || candidatePos === 'closer') {
      position = candidatePos;
    }
  }
  return {
    template: 'interactive-slot',
    brief: (ds.text || '').trim() || '',
    anchorVerb,
    position,
    // Modality is stamped by the SketchSurface tool handler after import,
    // since it lives at lesson scope (set_modality) not per-slot in the brief.
  };
}

/**
 * Composed experience (Kula Create Workshop). The Composer emits the spec
 * directly (title/subtitle/intent/blocks/scoring/conclusion), so this importer
 * is a thin normaliser: stamp the template, default the envelope, and fill any
 * missing block id with a stable index-based one. Block ids are NOT regenerated
 * when present — refs point at them. Grammar is checked downstream by
 * validateSlideData (its composed-experience branch runs runCompositionChecks).
 */
export function buildComposedExperience(ds: DesignerSlide, warnings: string[]): ComposedExperienceSlideData {
  const raw = (ds as { data?: unknown }).data as Partial<ComposedExperienceSlideData> | undefined;
  const blocksIn = Array.isArray(raw?.blocks) ? raw!.blocks : [];
  if (!raw || blocksIn.length === 0) {
    warnings.push('composed-experience: missing or empty `blocks` array');
  }
  const blocks = blocksIn.map((b, i) => {
    const block = (b && typeof b === 'object' ? b : {}) as CompositionBlock;
    if (!block.id) {
      const generated = `block-${i + 1}`;
      warnings.push(`composed-experience: block ${i + 1} had no id; assigned "${generated}"`);
      return { ...block, id: generated } as CompositionBlock;
    }
    return block;
  });
  const scoring = raw?.scoring && typeof raw.scoring === 'object'
    ? raw.scoring
    : { mode: 'mean' as const, blocks: [] };

  return {
    template: 'composed-experience',
    title: raw?.title ?? '',
    subtitle: raw?.subtitle ?? '',
    intent: raw?.intent ?? '',
    blocks,
    scoring,
    ...(raw?.conclusion ? { conclusion: raw.conclusion } : {}),
  };
}

/** Look up an importer by template id. New templates register here as they land. */
export const SCENARIO_IMPORTERS: Record<string, ScenarioImporter> = {
  'composed-experience': buildComposedExperience as ScenarioImporter,
  'reveal-predict': buildRevealPredict as ScenarioImporter,
  'confidence-meter': buildConfidenceMeter as ScenarioImporter,
  'reflect-act': buildReflectAct as ScenarioImporter,
  'before-after-bridge': buildBeforeAfterBridge as ScenarioImporter,
  'spot-the-error': buildSpotTheError as ScenarioImporter,
  'claim-evidence': buildClaimEvidence as ScenarioImporter,
  'gallery-walk': buildGalleryWalk as ScenarioImporter,
  'perspective-lenses': buildPerspectiveLenses as ScenarioImporter,
  'scaffolded-investigation': buildScaffoldedInvestigation as ScenarioImporter,
  'layered-reveal': buildLayeredReveal as ScenarioImporter,
  'timeline-navigator': buildTimelineNavigator as ScenarioImporter,
  'progressive-builder': buildProgressiveBuilder as ScenarioImporter,
  'guided-sandbox': buildGuidedSandbox as ScenarioImporter,
  'drag-sort': buildDragSort as ScenarioImporter,
  'tension-mapper': buildTensionMapper as ScenarioImporter,
  'scenario-simulation': buildScenarioSimulation as ScenarioImporter,
  'teach-back': buildTeachBack as ScenarioImporter,
  'hotspot-explorer': buildHotspotExplorer as ScenarioImporter,
  'source-examination': buildSourceExamination as ScenarioImporter,
  'mark-this-answer': buildMarkThisAnswer as ScenarioImporter,
  'revision-exam': buildRevisionExam as ScenarioImporter,
  bridge: buildBridge as ScenarioImporter,
  'interactive-slot': buildInteractiveSlot as ScenarioImporter,
};
